import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  TrendingUp,
  CalendarClock,
  CalendarCheck,
  UserX,
  BellOff,
  PhoneOutgoing,
  CheckCircle2,
  RefreshCw,
  XCircle,
  CalendarPlus,
  Send,
  Reply,
  Percent,
  CalendarCheck2,
  DollarSign,
  Trophy,
  Wallet,
} from 'lucide-react'

import { api, ApiError } from '@/lib/api'
import { formatCurrencyBRL, formatPercent } from '@/lib/format'
import { noShowRate, ticketMedio, totalMeetingsScheduled } from '@/lib/dashboard-metrics'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useAuthStore } from '@/stores/auth'
import { AiAssistantWidget } from '@/components/AiAssistantWidget'
import { AttendanceChart } from '@/components/AttendanceChart'
import { DashboardFilters, type DashboardFiltersValue } from '@/components/DashboardFilters'
import { KpiCard } from '@/components/KpiCard'
import { AiPerformancePanel } from '@/components/dashboard/AiPerformancePanel'
import { AppointmentStatusDonut } from '@/components/dashboard/AppointmentStatusDonut'
import { CampaignComparisonTable } from '@/components/dashboard/CampaignComparisonTable'
import { ColdRateCard } from '@/components/dashboard/ColdRateCard'
import { CommercialInsightCallout } from '@/components/dashboard/CommercialInsightCallout'
import { CommercialResultsPanel } from '@/components/dashboard/CommercialResultsPanel'
import { CommercialRoiPanel } from '@/components/dashboard/CommercialRoiPanel'
import { CostsSummaryPanel } from '@/components/dashboard/CostsSummaryPanel'
import { DailyAiEvolutionChart } from '@/components/dashboard/DailyAiEvolutionChart'
import { LeadQualificationFunnel } from '@/components/dashboard/LeadQualificationFunnel'
import { LeadQualityDonut } from '@/components/dashboard/LeadQualityDonut'
import { LossReasonChart } from '@/components/dashboard/LossReasonChart'
import { OpeningTemplateEffectiveness } from '@/components/dashboard/OpeningTemplateEffectiveness'
import { QuickSummaryPanel } from '@/components/dashboard/QuickSummaryPanel'
import { RecentInteractionsList } from '@/components/dashboard/RecentInteractionsList'
import { SdrComparisonTable } from '@/components/dashboard/SdrComparisonTable'
import { UnknownMeetingsTable } from '@/components/UnknownMeetingsTable'
import { useDropdownOptions } from '@/hooks/useDropdownOptions'

import type {
  AiAssistantPageContext,
  CampaignListResponse,
  DashboardKpisResponse,
  DashboardSdrComparisonResponse,
  SellerListResponse,
} from '@sdr-agend/admin-schemas'

/**
 * `/admin/dashboard` — Stories 4.7a (Visual MVP) + 4.7b (polish).
 *
 * 4.7a delivered: layout, KPI cards, SVG chart, UNKNOWN table, skeleton +
 * error states, "N/A" microcopy, filters in disabled state.
 *
 * 4.7b adds:
 *   - Functional filters (period preset + custom range + seller dropdown)
 *   - Sellers fetched from Story 3.6's `/admin/sellers` endpoint
 *   - TanStack Query staleTime 30s + refetch on window focus
 *   - Query params propagated to `GET /admin/dashboard/kpis`
 *
 * Story 4.6 (deferred) will populate `meetings.outcome` so the ratios stop
 * being `null` and the chart shows real bars.
 */

const DEFAULT_FILTERS: DashboardFiltersValue = {
  preset: 'month',
  customFrom: '',
  customTo: '',
  sellerId: '',
  campaignId: '',
  sdrId: '',
}

/**
 * 2026-08-04 (user request) — "Hoje"/"Semana" calculavam o início do dia em
 * UTC puro (`Date.UTC(now.getUTCFullYear(), ...)`), enquanto "Mês" já ancora
 * em São Paulo desde o fix anterior (`monthStart()` em `dashboard.ts`). Brasil
 * não tem DST desde 2019 — mesma constante/técnica usada lá (soma o offset,
 * extrai os componentes UTC do "dia local", subtrai o offset de volta).
 */
const SAO_PAULO_UTC_OFFSET_MIN = -180

export function spDayStart(instant: Date): Date {
  const spInstant = new Date(instant.getTime() + SAO_PAULO_UTC_OFFSET_MIN * 60_000)
  return new Date(
    Date.UTC(spInstant.getUTCFullYear(), spInstant.getUTCMonth(), spInstant.getUTCDate(), 0, 0, 0, 0) -
      SAO_PAULO_UTC_OFFSET_MIN * 60_000,
  )
}

interface DashboardErrorProps {
  error: unknown
}

function DashboardError({ error }: DashboardErrorProps): React.ReactElement {
  const msg = error instanceof ApiError ? `${error.status}` : 'erro desconhecido'
  return (
    <Card>
      <CardHeader>
        <CardTitle>Não conseguimos carregar o painel</CardTitle>
        <CardDescription>
          Tente atualizar a página em instantes. Se persistir, fale com o suporte ({msg}).
        </CardDescription>
      </CardHeader>
    </Card>
  )
}

function DashboardSkeleton(): React.ReactElement {
  return (
    <div className="space-y-4" data-testid="dashboard-skeleton">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-20 animate-pulse rounded-lg border border-t-2 bg-muted/40" />
        ))}
      </div>
      <div className="h-40 animate-pulse rounded-md border bg-muted/40" />
      <div className="h-32 animate-pulse rounded-md border bg-muted/40" />
    </div>
  )
}

/**
 * Compute the actual `from` / `to` ISO strings to send to the backend
 * based on the user-selected preset. Returns `null` when `custom` is
 * selected but the user hasn't filled both date inputs yet — caller
 * skips the API call until the period is complete.
 */
export function resolvePeriod(
  filters: DashboardFiltersValue,
): { from?: string; to?: string } | null {
  const now = new Date()
  switch (filters.preset) {
    case 'today': {
      return { from: spDayStart(now).toISOString(), to: now.toISOString() }
    }
    case 'week': {
      const start = new Date(spDayStart(now).getTime() - 7 * 24 * 60 * 60 * 1000)
      return { from: start.toISOString(), to: now.toISOString() }
    }
    case 'month': {
      // Default month-to-date — backend defaults to this when params absent.
      return {}
    }
    case 'custom': {
      if (filters.customFrom === '' || filters.customTo === '') return null
      const fromDate = new Date(`${filters.customFrom}T00:00:00.000Z`)
      const toDate = new Date(`${filters.customTo}T23:59:59.999Z`)
      if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
        return null
      }
      return { from: fromDate.toISOString(), to: toDate.toISOString() }
    }
  }
}

function buildKpisUrl(filters: DashboardFiltersValue): string | null {
  const period = resolvePeriod(filters)
  if (period === null) return null
  const params = new URLSearchParams()
  if (period.from !== undefined) params.set('from', period.from)
  if (period.to !== undefined) params.set('to', period.to)
  if (filters.sellerId !== '') params.set('sellerId', filters.sellerId)
  if (filters.campaignId !== '') params.set('campaignId', filters.campaignId)
  // Story 12.10 (Epic E12) — capability already existed since Story 12.7
  // (AC1: `sdrId` optional for admin/manager); this is the missing UI wire.
  if (filters.sdrId !== '') params.set('sdrId', filters.sdrId)
  const qs = params.toString()
  return qs === '' ? '/admin/dashboard/kpis' : `/admin/dashboard/kpis?${qs}`
}

/**
 * Story 12.9 — mesmo período de `buildKpisUrl`, mas SEM `sellerId`/
 * `campaignId`: o comparativo agrupa por SDR, não faz sentido filtrar por
 * vendedor/campanha (o endpoint dedicado nem aceita esses parâmetros).
 */
function buildSdrComparisonUrl(filters: DashboardFiltersValue): string | null {
  const period = resolvePeriod(filters)
  if (period === null) return null
  const params = new URLSearchParams()
  if (period.from !== undefined) params.set('from', period.from)
  if (period.to !== undefined) params.set('to', period.to)
  const qs = params.toString()
  return qs === '' ? '/admin/dashboard/sdr-comparison' : `/admin/dashboard/sdr-comparison?${qs}`
}

export function DashboardPage(): React.ReactElement {
  const user = useAuthStore((s) => s.user)
  const name = user?.name ?? 'Operador'

  const [filters, setFilters] = useState<DashboardFiltersValue>(DEFAULT_FILTERS)

  // Sellers list for the dropdown — Story 3.6 endpoint.
  const { options: sellerOptions, isLoading: sellersLoading } = useDropdownOptions<
    SellerListResponse,
    SellerListResponse['sellers'][number]
  >({
    queryKey: ['sellers-active-dropdown'],
    url: '/admin/sellers?active=true',
    selectItems: (r) => r.sellers,
    mapOption: (s) => ({ id: s.id, name: s.name }),
  })

  // Campaigns list for the dropdown (2026-07-16, user request) — all
  // campaigns regardless of type/active, so a manager can still pull
  // historical numbers for a since-deactivated campaign.
  //
  // QA fix: queryKey MUST start with `['admin-campaigns', ...]` and match
  // the no-filter shape `{ typeFilter: '', activeFilter: '' }` used by
  // Campaigns.tsx's default view — CampaignDetail.tsx's create/update/
  // delete/activate/deactivate mutations call
  // `invalidateQueries({ queryKey: ['admin-campaigns'] })`, which only
  // matches queries whose key starts with that same prefix. A distinct key
  // here would silently skip that invalidation, showing a stale campaign
  // name (or an already-deactivated campaign) in this dropdown for up to
  // `staleTime` after an edit made elsewhere in the admin panel. Sharing the
  // exact key also lets this reuse Campaigns.tsx's cache entry instead of a
  // second redundant fetch when both pages are visited in the same session.
  const { options: campaignOptions, isLoading: campaignsLoading } = useDropdownOptions<
    CampaignListResponse,
    CampaignListResponse['campaigns'][number]
  >({
    queryKey: ['admin-campaigns', { typeFilter: '', activeFilter: '' }],
    url: '/admin/campaigns',
    selectItems: (r) => r.campaigns,
    mapOption: (c) => ({ id: c.id, name: c.name }),
  })

  // KPI query — re-fires whenever the URL changes (filters mutate).
  // MEMOIZADO por `filters`: `resolvePeriod` usa `new Date()` em `to=now`, então
  // sem o memo a URL mudaria a cada render (now muda no ms) → queryKey instável →
  // loop infinito de refetch ("today"/"week" nunca carregavam).
  const kpisUrl = useMemo(() => buildKpisUrl(filters), [filters])
  const { data, isLoading, isError, error } = useQuery<DashboardKpisResponse>({
    queryKey: ['dashboard-kpis', kpisUrl ?? '__skipped__'],
    queryFn: async () => {
      // `enabled: false` below ensures this never runs with `null`. Type
      // narrowing requires the explicit guard.
      if (kpisUrl === null) {
        throw new Error('kpisUrl is null — query should be disabled')
      }
      return api<DashboardKpisResponse>(kpisUrl)
    },
    enabled: kpisUrl !== null,
    staleTime: 30 * 1000, // 30s — Story 4.7b §AC #5
    refetchOnWindowFocus: true,
  })

  // Story 12.9 — comparativo de carteiras, admin/manager only. Endpoint
  // dedicado (decisão @po, ver docs/stories/12.9.story.md) — query separada
  // da principal, só habilitada pra quem tem permissão de vê-la (o backend
  // já retorna 403 pras outras roles, mas evitar disparar a chamada é UX,
  // não segurança — a `rbac()` no backend é a barreira real).
  const isSdrComparisonVisible = user?.role === 'admin' || user?.role === 'manager'
  const sdrComparisonUrl = useMemo(() => buildSdrComparisonUrl(filters), [filters])
  const {
    data: sdrComparisonData,
    isLoading: sdrComparisonLoading,
    isError: sdrComparisonIsError,
  } = useQuery<DashboardSdrComparisonResponse>({
    queryKey: ['dashboard-sdr-comparison', sdrComparisonUrl ?? '__skipped__'],
    queryFn: async () => {
      if (sdrComparisonUrl === null) {
        throw new Error('sdrComparisonUrl is null — query should be disabled')
      }
      return api<DashboardSdrComparisonResponse>(sdrComparisonUrl)
    },
    enabled: isSdrComparisonVisible && sdrComparisonUrl !== null,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  })

  // Story 12.10 (Epic E12) — the admin/manager-only SDR filter dropdown
  // reuses the SAME `sdrComparisonData` fetched above for the comparison
  // table, instead of firing a second request against an endpoint that
  // already returns `{sdrId, sdrName}` per row. Zero extra network cost.
  const sdrFilterOptions = useMemo(
    () =>
      (sdrComparisonData?.rows ?? []).map((r) => ({
        id: r.sdrId,
        name: r.sdrName,
      })),
    [sdrComparisonData],
  )

  // 2026-08-03 (user request) — assistente de IA precisa do MESMO período já
  // resolvido pelo backend (`data.period`), não do preset cru, pra reasoning
  // consistente com o que está na tela (ex.: preset "month" não tem from/to
  // explícito — o backend resolve month-to-date e ecoa em `period`).
  const dashboardPageContext: AiAssistantPageContext | null =
    data !== undefined
      ? {
          page: 'dashboard',
          from: data.period.from,
          to: data.period.to,
          ...(filters.sellerId !== '' ? { sellerId: filters.sellerId } : {}),
          ...(filters.campaignId !== '' ? { campaignId: filters.campaignId } : {}),
        }
      : null

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Painel Comercial — IA SDR</h1>
        <p className="text-sm text-muted-foreground">
          Visão geral da performance de {name}. A taxa de comparecimento aparece após
          as primeiras reuniões serem finalizadas e classificadas pelo vendedor.
        </p>
      </div>

      <DashboardFilters
        value={filters}
        onChange={setFilters}
        sellers={sellerOptions}
        sellersLoading={sellersLoading}
        campaigns={campaignOptions}
        campaignsLoading={campaignsLoading}
        showSdrFilter={isSdrComparisonVisible}
        sdrs={sdrFilterOptions}
        sdrsLoading={isSdrComparisonVisible && sdrComparisonLoading}
      />

      {kpisUrl === null && (
        <Card>
          <CardHeader>
            <CardTitle>Selecione o período</CardTitle>
            <CardDescription>
              Para o filtro custom, escolha as datas de início e fim para carregar os
              KPIs.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {kpisUrl !== null && isLoading && <DashboardSkeleton />}

      {kpisUrl !== null && isError && <DashboardError error={error} />}

      {data !== undefined && !isLoading && !isError && (
        <>
          {/* Linha 1 (2026-08-17, user request via chamado — Haislann):
              resumo executivo "clean" — Agendamentos/Realizadas/No-show/
              Vendas/Conversão/Faturamento/Ticket médio, sem variação de
              período (essa comparação continua disponível mais abaixo, no
              painel "Reuniões e Resultado Comercial"). Tempos médios,
              "Saúde Comercial" e "Prioridades de Hoje" saíram da tela
              principal por serem operacionais demais pra essa visão — os
              dados continuam vindo na resposta e os componentes
              (`AiPerformancePanel`'s timing rows, `CommercialHealthGauge`,
              `TodayPrioritiesPanel`) seguem existindo pra um futuro
              dashboard dedicado de IA/SDR. */}
          <div
            className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-7"
            data-testid="top-kpis"
          >
            <KpiCard
              title="Agendamentos"
              description="No período"
              value={totalMeetingsScheduled(data.kpis)}
              accent="amber"
              icon={CalendarPlus}
              helpText="Todas as reuniões com data marcada no período, somando pendente + confirmada + compareceu + faltou + cancelada + sem resposta ao lembrete de confirmação — de qualquer origem (vendedor, GHL, IA) e qualquer campanha. Não conta reagendamentos (a reunião antiga vira uma nova linha, contaria em dobro)."
            />
            <KpiCard
              title="Realizadas"
              description="Compareceram"
              value={data.kpis.meetingsAttended}
              accent="sky"
              icon={CalendarCheck}
              helpText="Reuniões com data marcada no período cujo resultado final foi 'compareceu', classificado pelo vendedor."
            />
            <KpiCard
              title="No-show"
              description="Taxa"
              value={noShowRate(data.kpis.attendanceRate)}
              formatValue={formatPercent}
              accent="rose"
              icon={UserX}
              helpText="Faltou ÷ (compareceu + faltou), entre as reuniões já finalizadas no período. Complemento da taxa de comparecimento."
            />
            <KpiCard
              title="Vendas"
              description="Pipeline GHL"
              value={data.salesIndicator.count}
              accent="emerald"
              icon={Trophy}
              helpText='Oportunidades movidas para "Venda realizada" na pipeline do GHL no período selecionado.'
            />
            <KpiCard
              title="Conversão"
              description="Vendas ÷ realizadas"
              value={data.commercialHealthIndex.conversionRate}
              formatValue={(n) => `${Math.round(n)}%`}
              accent="violet"
              icon={Percent}
              helpText="Vendas ÷ reuniões compareceram no período. Pode passar de 100% pontualmente: vendas usam a data de fechamento, comparecimento usa a data da reunião — uma venda do período pode vir de uma reunião de outro período (por isso é limitada a 100%)."
            />
            <KpiCard
              title="Faturamento"
              description="Soma do valor"
              value={data.salesIndicator.valueSum}
              formatValue={formatCurrencyBRL}
              accent="blue"
              icon={DollarSign}
              helpText='Soma do campo "Valor" das oportunidades vendidas no período (mesmo valor exibido no card da oportunidade no GHL).'
            />
            <KpiCard
              title="Ticket médio"
              description="Faturamento ÷ vendas"
              value={ticketMedio(data.salesIndicator)}
              formatValue={formatCurrencyBRL}
              microcopy="Nenhuma venda sincronizada no período."
              accent="slate"
              icon={Wallet}
              helpText="Faturamento do período dividido pela quantidade de vendas do período."
            />
          </div>

          {/* Linha 2 — Funil Comercial (compacto, sem tempos médios/tabela
              por campanha — isso mora no card "Desempenho por Campanha"
              abaixo). QA fix (2026-07-31, user request): 6 caixas + setas
              precisam de mais largura do que QUALQUER arranjo de colunas
              iguais consegue dar sem cortar/espremer a última caixa — o
              funil ocupa a linha inteira sozinho. */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Funil Comercial</CardTitle>
              <CardDescription>
                Recebidos → Qualificados (Lead Novo) + confirmações da IA de qualquer origem, no
                período selecionado.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LeadQualificationFunnel
                data={data.leadQualificationFunnel}
                salesCount={data.salesIndicator.count}
                showDetails={false}
              />
            </CardContent>
          </Card>

          {/* Linha 3 — Desempenho por Campanha (tabela transposta a partir de
              leadQualificationFunnel.byCampaign + Insight Principal de IA
              logo abaixo) | Performance da IA (reaproveita openingTemplate-
              Effectiveness + tempos médios) | Evolução Diária. */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Desempenho por Campanha</CardTitle>
                <CardDescription>Compare a performance entre campanhas.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <CampaignComparisonTable data={data.leadQualificationFunnel} />
                <CommercialInsightCallout data={data.commercialInsight} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Performance da IA</CardTitle>
                <CardDescription>Indicadores de performance da IA SDR.</CardDescription>
              </CardHeader>
              <CardContent>
                <AiPerformancePanel
                  openingTemplateEffectiveness={data.openingTemplateEffectiveness}
                  leadQualificationFunnel={data.leadQualificationFunnel}
                  qualificationRate={data.commercialHealthIndex.qualificationRate}
                  showTimingRows={false}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Evolução Diária</CardTitle>
                <CardDescription>Leads, qualificados, agendamentos, no-show e vendas por dia.</CardDescription>
              </CardHeader>
              <CardContent>
                <DailyAiEvolutionChart data={data.dailyAiEvolution} />
              </CardContent>
            </Card>
          </div>

          {/* Linha 4 — Reuniões e Resultado Comercial | Qualidade dos Leads |
              Status dos Agendamentos | Motivos de Perda. */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Reuniões e Resultado Comercial</CardTitle>
                <CardDescription>Consolidado do período selecionado.</CardDescription>
              </CardHeader>
              <CardContent>
                <CommercialResultsPanel kpis={data.kpis} salesIndicator={data.salesIndicator} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Qualidade dos Leads</CardTitle>
                <CardDescription>Lead Novo — Alta, Média e Baixa.</CardDescription>
              </CardHeader>
              <CardContent>
                <LeadQualityDonut data={data.leadQualityBreakdown} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Status dos Agendamentos</CardTitle>
                <CardDescription>Confirmados, reagendados e cancelados.</CardDescription>
              </CardHeader>
              <CardContent>
                <AppointmentStatusDonut data={data.appointmentStatusBreakdown} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Motivos de Perda</CardTitle>
                <CardDescription>
                  Sincronizado do GHL — vazio até a configuração/sincronização rodar.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <LossReasonChart data={data.lossReasonBreakdown} />
              </CardContent>
            </Card>
          </div>

          {/* Linha 5 — Últimas Interações da IA | Impacto Financeiro (ROI). */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Últimas Interações da IA</CardTitle>
                <CardDescription>
                  Atividade mais recente — respeita os filtros de vendedor/campanha, não o de
                  período.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <RecentInteractionsList data={data.recentInteractions} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Impacto Financeiro</CardTitle>
                <CardDescription>Custo estimado convertido, comparado com o faturamento.</CardDescription>
              </CardHeader>
              <CardContent>
                <CommercialRoiPanel data={data.commercialRoi} />
              </CardContent>
            </Card>
          </div>

          {/* Redesign visual (2026-07-31) — tudo que a referência do usuário
              não mostra na visão principal, mas que continua tendo dado real
              e testes cobrindo: fica aqui embaixo, não é removido. */}
          <div className="border-t pt-2">
            <h2 className="text-base font-semibold tracking-tight">Detalhes adicionais</h2>
            <p className="text-sm text-muted-foreground">
              Métricas complementares — não fazem parte do resumo executivo acima.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <KpiCard
              title="Comparecimento"
              description="Período selecionado"
              value={data.kpis.attendanceRate}
              formatValue={formatPercent}
              accent="emerald"
              icon={TrendingUp}
              helpText='Reuniões com data marcada no período e já finalizadas: compareceu ÷ (compareceu + faltou), de qualquer origem, mesmo sem confirmação da IA. Reuniões ainda em aberto (pendente/confirmada) não entram nesta conta. Diferente de "Compareceram" no Funil Comercial, que conta só reuniões que passaram pelo fluxo de confirmação da IA.'
              status={
                data.kpis.attendanceRate === null
                  ? undefined
                  : data.kpis.attendanceRate >= 0.7
                    ? { tone: 'good', label: 'bom' }
                    : { tone: 'watch', label: 'atenção' }
              }
            />
            <KpiCard
              title="Em aberto"
              description="Pending + confirmadas"
              value={data.kpis.meetingsScheduled}
              microcopy=""
              accent="blue"
              icon={CalendarClock}
              helpText='Todas as reuniões com data marcada no período que ainda não foram finalizadas (status pendente ou confirmada) — de QUALQUER origem (vendedor, GHL, IA) e qualquer campanha. Não confundir com "Novos agendamentos" abaixo, que conta só o que a IA criou via Lead Novo.'
            />
            <KpiCard
              title="Realizadas"
              description="Compareceram"
              value={data.kpis.meetingsAttended}
              microcopy=""
              accent="sky"
              icon={CalendarCheck}
              helpText="Reuniões com data marcada no período cujo resultado final foi 'compareceu', classificado pelo vendedor."
            />
            <KpiCard
              title="No-shows"
              description="Faltaram"
              value={data.kpis.meetingsNoShow}
              microcopy=""
              accent="rose"
              icon={UserX}
              helpText="Reuniões com data marcada no período cujo resultado final foi 'faltou', classificado pelo vendedor."
            />
            <KpiCard
              title="Opt-outs"
              description="Pediram para sair"
              value={data.kpis.optOutsTotal}
              microcopy=""
              accent="amber"
              icon={BellOff}
              helpText="Clientes que pediram para sair (opt-out) via WhatsApp no período. Total geral do sistema — não filtra por vendedor ou campanha."
            />
            {/* Story 5.7 + 5.8 — Cold rate sobre os últimos 7 dias. */}
            <ColdRateCard />
          </div>

          {/* Story 10.5 (2026-07-28, user request) — "Vendas": contagem +
              faturamento das oportunidades movidas para "Venda realizada" na
              pipeline de vendas do GHL, sincronizadas via ghl-sale-sync.worker. */}
          <div>
            <h2 className="text-base font-semibold tracking-tight">Vendas</h2>
            <p className="text-sm text-muted-foreground">
              Sincronizado do GHL — oportunidades movidas para &quot;Venda realizada&quot; no
              período selecionado.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            <KpiCard
              title="Vendas realizadas"
              description="No período"
              value={data.salesIndicator.count}
              microcopy="Nenhuma venda sincronizada no período."
              accent="emerald"
              icon={Trophy}
              helpText='Contagem de oportunidades da pipeline de vendas do GHL movidas para o estágio "Venda realizada" no período selecionado, sincronizadas periodicamente pelo servidor.'
            />
            <KpiCard
              title="Faturamento"
              description="Soma do valor"
              value={data.salesIndicator.valueSum}
              formatValue={formatCurrencyBRL}
              microcopy="Nenhuma venda sincronizada no período."
              accent="blue"
              icon={DollarSign}
              helpText='Soma do campo "Valor" das oportunidades vendidas no período (mesmo valor exibido no card da oportunidade no GHL).'
            />
          </div>

          {/* Story 10.6 (2026-07-30, user request, AC3+AC4) — "Custos":
              estimativa de gasto com IA (OpenAI/Claude/Gemini) + WhatsApp
              API (Meta) no período. Explicitamente NÃO é custo de
              infraestrutura de servidor (decisão de produto) nem um valor
              de billing exato — ver `CostsSummaryPanel` pro texto completo. */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Custos</CardTitle>
              <CardDescription>
                Estimativa de gasto com IA e WhatsApp no período selecionado — não inclui
                infraestrutura de servidor.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CostsSummaryPanel data={data.costs} />
            </CardContent>
          </Card>

          {/* 2026-07-15 (user request) — "Resultados da IA": o que a IA fez
              proativamente no período, separado das métricas de reunião
              acima (que medem o resultado FINAL, não a ação da IA). */}
          <div>
            <h2 className="text-base font-semibold tracking-tight">Resultados da IA</h2>
            <p className="text-sm text-muted-foreground">
              O que a IA fez no período selecionado.
            </p>
          </div>
          <div
            className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5"
            data-testid="ai-results-kpis"
          >
            <KpiCard
              title="Clientes chamados"
              description="Contatados pela IA"
              value={data.kpis.customersContactedByAi}
              microcopy=""
              accent="violet"
              icon={PhoneOutgoing}
              helpText="Clientes distintos com uma conversa NOVA iniciada pela IA no período (exclui cliente que escreveu primeiro, e testes manuais da operação)."
            />
            <KpiCard
              title="Novos agendamentos"
              description="Lead Novo, feitos pela IA"
              value={data.kpis.meetingsBookedByAi}
              microcopy=""
              accent="blue"
              icon={CalendarPlus}
              helpText='Só reuniões NOVAS que a IA criou do zero, no fluxo de qualificação de Lead Novo, no período. NÃO inclui reagendamentos (contam em "Reagendamentos" ao lado), reuniões vindas do GHL/vendedor, nem da campanha de Confirmação — por isso costuma ser um número bem menor que "Em aberto" no topo do painel, que conta reuniões de qualquer origem.'
            />
            <KpiCard
              title="Confirmados"
              description="Confirmaram pela IA"
              value={data.kpis.meetingsConfirmedByAi}
              microcopy=""
              accent="emerald"
              icon={CheckCircle2}
              helpText="Conversas em que o cliente confirmou a reunião pelo chat com a IA, no período."
            />
            <KpiCard
              title="Reagendamentos"
              description="No período"
              value={data.kpis.reschedulesTotal}
              microcopy=""
              accent="amber"
              icon={RefreshCw}
              helpText="Todos os reagendamentos registrados no período, de qualquer origem (pelo chat com a IA OU via GHL) — diferente dos outros cards desta seção, não é exclusivo da IA."
            />
            <KpiCard
              title="Cancelamentos"
              description="Detectados pela IA"
              value={data.kpis.cancellationsCaughtByAi}
              microcopy=""
              accent="rose"
              icon={XCircle}
              helpText="Conversas em que o cliente cancelou a reunião pelo chat com a IA, no período."
            />
          </div>

          {/* 2026-07-20 (user request) — "Resumo Rápido" do que a IA fez. O
              funil de qualificação completo (tempos médios + tabela por
              campanha) não é repetido aqui — a versão compacta mora na
              Linha 2 acima, e a comparação por campanha na Linha 3
              (`CampaignComparisonTable`), evitando mostrar o mesmo
              `leadQualificationFunnel` duas vezes na mesma página. */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Resumo Rápido</CardTitle>
              <CardDescription>O que a IA fez, de relance.</CardDescription>
            </CardHeader>
            <CardContent>
              <QuickSummaryPanel
                customersContactedByAi={data.kpis.customersContactedByAi}
                meetingsConfirmedByAi={data.kpis.meetingsConfirmedByAi}
                reschedulesTotal={data.kpis.reschedulesTotal}
                cancellationsCaughtByAi={data.kpis.cancellationsCaughtByAi}
              />
            </CardContent>
          </Card>

          {/* 2026-07-21 (user request) — efetividade do template de abertura
              (Lead Novo): Mensagens Enviadas/Respondidas/Taxa de
              Resposta/Reuniões Agendadas/Taxa de Agendamento, globalmente e
              por variante A/B/C (quando a campanha tiver teste configurado).
              QA re-review fix (2026-07-21): a métrica não tem quebra por
              vendedor (é agregada por campanha+variante, sem atribuição a
              nenhum vendedor específico) — por decisão do usuário, a seção
              inteira fica escondida pra role=vendedor em vez de mostrar o
              desempenho do time inteiro (o backend já redige o campo pra
              esse role, isso aqui é só a camada de UI).

              Story 12.7 (Epic E12, AC7/T14) — `sdr` NÃO entra nessa condição
              de propósito: ao contrário de `vendedor`, o backend NÃO redige
              este campo pra `sdr` (`queryOpeningTemplateEffectiveness` já
              aplica o filtro de carteira via `sdrId` — Dev Notes da 12.7).
              Uma SDR vê a efetividade do SEU recorte de leads, não um campo
              vazio nem o dado da empresa inteira — não há nada aqui pra
              esconder, reaproveitando o mesmo padrão condicional (não um
              novo). O dropdown "Vendedor" abaixo (`DashboardFilters`)
              também não recebeu tratamento especial pra `sdr` pelo mesmo
              motivo que já vale hoje pra `vendedor`: o filtro é
              estruturalmente ignorado no backend pra sessões escopadas
              (clamp em `resolveSdrScope`), sem precisar de lógica de UI
              extra pra escondê-lo — mesma decisão já tomada aqui pra
              `vendedor`, não um padrão novo. */}
          {user?.role !== 'vendedor' && (
            <>
              <div>
                <h2 className="text-base font-semibold tracking-tight">
                  Efetividade do template de abertura
                </h2>
                <p className="text-sm text-muted-foreground">
                  Lead Novo — do envio da 1ª mensagem até o agendamento, no período selecionado.
                </p>
              </div>
              <div
                className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5"
                data-testid="opening-template-kpis"
              >
                <KpiCard
                  title="Mensagens Enviadas"
                  description="Abertura entregue"
                  value={data.openingTemplateEffectiveness.messagesSent}
                  microcopy=""
                  accent="blue"
                  icon={Send}
                />
                <KpiCard
                  title="Mensagens Respondidas"
                  description="Cliente respondeu"
                  value={data.openingTemplateEffectiveness.messagesAnswered}
                  microcopy=""
                  accent="sky"
                  icon={Reply}
                />
                <KpiCard
                  title="Taxa de Resposta"
                  description="Respondidas / Enviadas"
                  value={data.openingTemplateEffectiveness.responseRate}
                  formatValue={formatPercent}
                  microcopy="Sem mensagens enviadas no período."
                  accent="violet"
                  icon={Percent}
                />
                <KpiCard
                  title="Reuniões Agendadas"
                  description="A partir da abertura"
                  value={data.openingTemplateEffectiveness.meetingsScheduled}
                  microcopy=""
                  accent="emerald"
                  icon={CalendarCheck2}
                />
                <KpiCard
                  title="Taxa de Agendamento"
                  description="Agendadas / Respondidas"
                  value={data.openingTemplateEffectiveness.schedulingRate}
                  formatValue={formatPercent}
                  microcopy="Sem mensagens respondidas no período."
                  accent="amber"
                  icon={Percent}
                />
              </div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Por template / variante</CardTitle>
                  <CardDescription>
                    Comparativo de abertura por campanha e variante A/B/C (quando configurada).
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <OpeningTemplateEffectiveness data={data.openingTemplateEffectiveness} />
                </CardContent>
              </Card>
            </>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Comparecimento por vendedor</CardTitle>
              <CardDescription>
                Comparativo no período selecionado. Inclui apenas reuniões já
                finalizadas.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AttendanceChart data={data.bySellerAttendance} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Reuniões aguardando callback</CardTitle>
              <CardDescription>
                Reuniões já realizadas em que o vendedor ainda não respondeu se
                compareceu.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <UnknownMeetingsTable data={data.unknownMeetings} />
            </CardContent>
          </Card>
        </>
      )}

      {/* Story 12.9 (Epic E12) — comparativo de carteiras por SDR,
          admin/manager only. Independente do carregamento da query principal
          de KPIs (tem seu próprio loading/error) — decisão @po: endpoint
          dedicado, ver docs/stories/12.9.story.md. */}
      {isSdrComparisonVisible && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Comparativo de carteiras por SDR</CardTitle>
            <CardDescription>
              Volume e atividade de cada carteira no período selecionado.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sdrComparisonLoading && (
              <div
                className="flex h-24 items-center justify-center text-sm text-muted-foreground"
                data-testid="sdr-comparison-loading"
              >
                Carregando…
              </div>
            )}
            {sdrComparisonIsError && (
              <div
                className="flex h-24 items-center justify-center text-sm text-destructive"
                data-testid="sdr-comparison-error"
              >
                Não foi possível carregar o comparativo de carteiras.
              </div>
            )}
            {sdrComparisonData !== undefined && !sdrComparisonLoading && !sdrComparisonIsError && (
              <SdrComparisonTable data={sdrComparisonData} />
            )}
          </CardContent>
        </Card>
      )}

      <AiAssistantWidget pageContext={dashboardPageContext} />
    </div>
  )
}

export default DashboardPage
